import { z } from 'zod';
import { isValidPhone } from '../lib/phone';

/**
 * The Telegram login's two forms, checked before Telegram is asked: a send
 * with a blank name used to reach the API, fail validation, and read as
 * "unreachable". The bounds mirror the telegram-login contract.
 */
export const accountSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Display name is required.')
    .max(40, 'Display name is at most 40 characters.'),
  apiId: z.string().trim().regex(/^\d+$/, 'API ID is the number from my.telegram.org.'),
  apiHash: z.string().trim().min(8, 'API hash is required.'),
  phone: z.string().refine(isValidPhone, 'Enter the phone number with its country code.'),
});

export type AccountForm = z.input<typeof accountSchema>;

export const EMPTY_ACCOUNT: AccountForm = { name: '', apiId: '', apiHash: '', phone: '' };

/** Step two is the code Telegram sent; step three the 2FA password. */
export const secretSchema = {
  2: z.object({
    secret: z
      .string()
      .trim()
      .min(3, 'Enter the code Telegram sent.')
      .max(12, 'That code is too long.'),
  }),
  3: z.object({ secret: z.string().min(1, 'Enter the two-factor password.') }),
} as const;

export type SecretForm = z.input<(typeof secretSchema)[2]>;

/** The first message to show, in field order. */
export function firstLoginError(
  errors: Partial<Record<string, { message?: string | undefined } | undefined>>,
): string | undefined {
  for (const name of ['name', 'apiId', 'apiHash', 'phone', 'secret']) {
    const message = errors[name]?.message;
    if (message !== undefined) return message;
  }
  return undefined;
}
